import type { CommonApi } from '@/v2/services/types';
import styled from '@emotion/styled';
import { Box, Flex, Heading } from '@qga/roo-ui/components';
import { themeGet } from 'styled-system';

import { BEST_RATE_TEXT } from '@shared/v2/constants/packages';
import useSearchQuery from '@shared/v2/hooks/useSearchQuery';

import PackageHeroSummary from '@/components/PackageHeroSummary';
import useProcuredOffer from '@/components/PackagePage/hooks/useProcuredOffer';
import PricePerTraveller from '@/components/PricePerTraveller';
import Text from '@/components/Text';
import usePropertyContent from '@/hooks/usePropertyContent';
import useAddDefaultFlightCharges from '@/v2/components/pages/PackageViewV2/hooks/useAddDefaultFlightPrices';
import usePrependFlightInclusion from '@/v2/components/pages/PackageViewV2/hooks/usePrependFlightInclusion';
import PackageDetails from '@/v2/components/ui/PackageDetails';
import { PackageInclusions } from '@/v2/components/ui/PackageInclusions';
import Inclusion from '@/v2/components/ui/PackageInclusions/Inclusion';
import { UI } from '@/v2/types';
import {
  convertedHotelOfferInclusion,
  convertLeadInOfferInclusion,
} from '@/v2/utils/inclusions';

const StyledHeading = styled(Heading.h4)`
  margin: 0;
  font-family: ${themeGet('headerFontFamily')};
`;

const ColumnFlex = styled(Flex)`
  flex-direction: column;
`;

export type RoomOptionDetailsProps = {
  packageName?: string;
  offer: UI.RoomOptionOffer;
  roomType: CommonApi.ParsedResponse.RoomType;
  title?: string;
  limitInclusions?: boolean;
};

const RoomOptionDetails = ({
  offer,
  packageName,
  roomType,
  title,
  limitInclusions = false,
}: RoomOptionDetailsProps) => {
  const { leadInOffer, hotelOffer } = offer;

  const { searchQuery } = useSearchQuery();
  const { adults, children, infants } = searchQuery || {};

  const propertyContent: UI.Property = usePropertyContent();
  const procuredOffer: UI.ProcuredOffer | undefined = useProcuredOffer();
  const charges = useAddDefaultFlightCharges(offer?.charges);

  const baseInclusions =
    leadInOffer?.inclusions?.map(convertLeadInOfferInclusion) ??
    hotelOffer?.inclusions?.map(convertedHotelOfferInclusion);
  const inclusions = usePrependFlightInclusion(baseInclusions);

  const inclusionLimit = limitInclusions ? 4 : Infinity;

  return (
    <ColumnFlex gap="24px" data-testid="PACKAGE_OPTION_ROOM_OPTION_DETAILS">
      <ColumnFlex alignItems="flex-start" gap="16px">
        <ColumnFlex gap="4px">
          <Text
            bold
            fontSize={['14px', null, '16px']}
            lineHeight={['20px', null, '24px']}
            letterSpacing="0.1px"
          >
            {title ?? 'Your package option'}
          </Text>
          <StyledHeading
            fontSize={['18px', null, '24px']}
            lineHeight={['24px', null, '30px']}
          >
            {packageName ?? BEST_RATE_TEXT}
          </StyledHeading>
        </ColumnFlex>
        <ColumnFlex gap="4px">
          <Text
            bold
            fontSize={['14px', null, '16px']}
            lineHeight={['20px', null, '24px']}
            letterSpacing="0.1px"
          >
            Room type
          </Text>
          <Inclusion icon="stay" title={roomType.name} />
        </ColumnFlex>
        <ColumnFlex gap="8px" alignItems="flex-start">
          <PackageInclusions
            inclusions={inclusions ?? []}
            numberToShow={inclusionLimit}
          />
          <PackageDetails
            propertyContent={propertyContent}
            procuredOffer={procuredOffer}
            leadInOffer={leadInOffer}
            inclusions={inclusions ?? []}
          />
        </ColumnFlex>
      </ColumnFlex>
      <Box>
        {/* @ts-ignore bad JS type */}
        <PackageHeroSummary searchQuery={searchQuery} showNights exact />
        <PricePerTraveller
          exactTotalPrice
          travellers={{ adults, children, infants }}
          perTraveller={charges?.perTraveller}
          total={charges?.total}
        />
      </Box>
    </ColumnFlex>
  );
};

export default RoomOptionDetails;
